import axios from 'axios';

const API_URL = "https://test2.dingai.io/api";

const adminSurveyService = {
  // Get admin token from cookies
  getAdminToken: () => {
    return document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];
  },

  // Fetch all admin surveys
  getAllAdminSurveys: async () => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];
    if (!token) throw new Error("Admin token not available");

    try {
      const response = await axios.get(`${API_URL}/survey/admin/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const surveys = response.data?.data || [];

      // Map _id to id for consistency
      return surveys.map((survey) => ({
        ...survey,
        id: survey._id,
      }));
    } catch (error) {
      console.error("API call failed:", error.response?.data || error.message);
      throw error;
    }
  },

  // Get admin survey by ID
  getAdminSurveyById: async (id) => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];
    if (!token) throw new Error("Admin token not available");

    try {
      const response = await axios.get(`${API_URL}/survey/admin/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = response.data?.data;
      return {
        ...data,
        id: data._id,
      };
    } catch (error) {
      console.error(`Failed to fetch admin survey with ID ${id}:`, error.response?.data || error.message);
      throw error;
    }
  },

  // Create a new admin survey
  createAdminSurvey: async (surveyData) => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];
    if (!token) throw new Error("Admin token not available");

    try {
      const response = await axios.post(`${API_URL}/survey/admin/create`, surveyData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error("Failed to create admin survey:", error.response?.data || error.message);
      throw error;
    }
  },

  // Update an existing admin survey
  updateAdminSurvey: async (id, surveyData) => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];
    if (!token) throw new Error("Admin token not available");

    try {
      const response = await axios.put(`${API_URL}/survey/admin/update/${id}`, surveyData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error(`Failed to update admin survey with ID ${id}:`, error.response?.data || error.message);
      throw error;
    }
  },

  // Delete an admin survey
  deleteAdminSurvey: async (id) => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];
    if (!token) throw new Error("Admin token not available");

    try {
      const response = await axios.delete(`${API_URL}/survey/admin/delete/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error(`Failed to delete admin survey with ID ${id}:`, error.response?.data || error.message);
      throw error;
    }
  },

  // Toggle admin survey status
  toggleAdminSurveyStatus: async (id, active) => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];
    if (!token) throw new Error("Admin token not available");

    try {
      const response = await axios.put(`${API_URL}/survey/admin/update/${id}`, { active }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error(`Failed to toggle admin survey status:`, error.response?.data || error.message);
      throw error;
    }
  },

  // Get admin survey analytics
  getAdminSurveyAnalytics: async (id) => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];
    if (!token) throw new Error("Admin token not available");

    try {
      const response = await axios.get(`${API_URL}/surveyAnswer/admin/analytics/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error(`Failed to fetch admin survey analytics:`, error.response?.data || error.message);
      throw error;
    }
  },

  // Generate AI insights for admin survey
  generateAdminSurveyInsights: async (surveyId) => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];
    if (!token) throw new Error("Admin token not available");

    try {
      const response = await axios.get(`${API_URL}/surveyAnswer/generateAdminSurveyInsight?surveyId=${surveyId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error(`Failed to generate admin survey insights:`, error.response?.data || error.message);
      throw error;
    }
  },

  // Get all survey answers for admin (for chat list)
  getAllAdminSurveyAnswers: async (surveyId) => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];
    if (!token) throw new Error("Admin token not available");

    try {
      const response = await axios.get(`${API_URL}/surveyAnswer/getAll?surveyId=${surveyId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error(`Failed to fetch admin survey answers:`, error.response?.data || error.message);
      throw error;
    }
  },

  // Get all chat messages for admin
  getAllAdminChatMessages: async (surveyAnswerId) => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];
    if (!token) throw new Error("Admin token not available");

    try {
      const response = await axios.get(`${API_URL}/chat/getAll?surveyAnswerId=${surveyAnswerId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error(`Failed to fetch admin chat messages:`, error.response?.data || error.message);
      throw error;
    }
  },
};

export default adminSurveyService;
